/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';
import moment from 'moment';

import { TS_FORMAT } from '../../Common/constants/constants';
import { AlertsType } from '../../Common/types/AlertsTypes';

import {
  stripDevicesFromPath,
  isHierarchyPathADevice,
  getLeafIdFromHierarchyPath,
  stripSystemChunkFromLocationHierarchyPath,
} from 'hierarchyUtils';
import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet, fetchPost, multipartFormHeaders } from 'FetchAPIs';

export * from './HierarchiesAPIs/assetTypesAPI';
export * from './HierarchiesAPIs/deviceTypesAPIs';
export * from './HierarchiesAPIs/devicesAPI';
export * from './HierarchiesAPIs/locationsAPI';
export * from './HierarchiesAPIs/organizationsAPI';
export * from './HierarchiesAPIs/sitesAPI';
export * from './HierarchiesAPIs/systemsAPI';
export * from './HierarchiesAPIs/hierarchyDashboardAPIs';
export * from './HierarchiesAPIs/availabilityAPI';

const baseHierarchiesAsengineURL = '/api/hierarchies/asengine';

export const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingDataItems: 'Error fetching data items. {message}',
  errorLoadingDimensions: 'Error loading dimensions. {message}',
  errorValidatingCSVFile: 'Error validating the CSV file. {message}',
  errorValidatingCSVRelationsFile: 'Error validating CSV relationships file. {message}',
  errorImportingCSVRelationsFile: 'Error importing CSV relationships file. {message}',
  errorImportingCSVFile: 'Error importing CSV file. {message}',
  errorLoadingItemDetails: 'Error loading item details. {message}',
});

/**
 * Function that validates a CSV file import syntax
 * @param {file} file The file to validate
 * @param {string} userEmailId The email of the user that is importing the file
 * @returns
 */
export const validateCSVImport = async (file: FormData, userEmailId: string): Promise<any> =>
  fetchPost(
    `${baseHierarchiesURL}/workspaces/import/validate`,
    file,
    {
      headers: multipartFormHeaders,
      userEmailId,
    },
    null
  ).catch(error => {
    throw new I18NError(messages.errorValidatingCSVFile, {
      message: formatErrorResponse(error),
    });
  });

/**
 * Function that imports a CSV file for the hierarchy
 * @param {file} file The file to validate
 * @param {string} userEmailId The email of the user that is importing the file
 * @returns
 */
export const hierarchyCSVImport = async (file: FormData, userEmailId: string): Promise<any> =>
  fetchPost(
    `${baseHierarchiesURL}/workspaces/import`,
    file,
    {
      headers: multipartFormHeaders,
      userEmailId,
    },
    null,
    response => response // work around so that we dont convert the response object to JSON
  ).catch(error => {
    throw new I18NError(messages.errorImportingCSVFile, {
      message: formatErrorResponse(error),
    });
  });

export const validateCSVRelationshipsImport = async (
  file: FormData,
  userEmailId: string
): Promise<void> =>
  fetchPost(
    `${baseHierarchiesURL}/workspaces/import/validate/relations`,
    file,
    {
      headers: multipartFormHeaders,
      userEmailId,
    },
    null
  ).catch(error => {
    throw new I18NError(messages.errorValidatingCSVRelationsFile, {
      message: formatErrorResponse(error),
    });
  });

export const importRelationshipsCSV = async (file: FormData, userEmailId: string): Promise<void> =>
  fetchPost(
    `${baseHierarchiesURL}/workspaces/import/relations`,
    file,
    {
      headers: multipartFormHeaders,
      userEmailId,
    },
    null
  ).catch(error => {
    throw new I18NError(messages.errorImportingCSVRelationsFile, {
      message: formatErrorResponse(error),
    });
  });

/**
 * Function that loads the details of a specific instance in the hierarchy
 * @returns existing hierarchy instance
 */
export const getHierarchyItem = async (hierarchyPath: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}${hierarchyPath}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingItemDetails, {
        message: formatErrorResponse(error),
      });
    });

export const getHierarchyDataItems = async (hierarchyPath: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}${hierarchyPath}/dataItems`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDataItems, {
        message: formatErrorResponse(error),
      });
    });

export const getHierarchyAlertData = async (
  hierarchyPath: string,
  dataItemName: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  timeGrain: string,
  groupBy: string,
  userEmailId: string
): Promise<AlertsType> => {
  const strippedHierarchyPath = stripSystemChunkFromLocationHierarchyPath(
    stripDevicesFromPath(hierarchyPath)
  );
  const entityTypesFilter: [{ dataItem: string[]; entities?: string[] }] = [
    {
      dataItem: [dataItemName],
    },
  ];
  if (isHierarchyPathADevice(hierarchyPath)) {
    entityTypesFilter[0].entities = [getLeafIdFromHierarchyPath(hierarchyPath)];
  }
  return fetchPost(
    `${baseHierarchiesAsengineURL}${strippedHierarchyPath}/queryalertdata`,
    {
      start_ts: startDate.format(TS_FORMAT),
      end_ts: endDate.format(TS_FORMAT),
      groupBy: Array.isArray(groupBy) ? groupBy : [groupBy],
      time_grain: timeGrain,
      entityTypesFilter,
    },
    { userEmailId },
    null
  )
    .then(res => res)
    .catch(error => console.error(error));
};

export const getHierarchyLevelDimensions = async (hierarchyPath: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}${hierarchyPath}/hierarchy/dimensions`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingDimensions, error);
    });
